import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { X, MessageSquare, Clock, Save, Trash2, Package, IndianRupee, Loader2, History, ChevronDown, ChevronUp, Upload, Image, Camera } from 'lucide-react';
import { formatDate, getStatusColor, getStatusLabel } from '../lib/utils';

const LeadDetailsModal = ({ lead, onClose }) => {
  const [leadData, setLeadData] = useState(lead);
  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderNote, setReminderNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [leadHistory, setLeadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Screenshot upload state
  const [conversionScreenshot, setConversionScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const fileInputRef = useRef(null);
  
  const user = authService.getCurrentUser();

  useEffect(() => {
    fetchRemarks();
  }, []);

  const fetchRemarks = async () => {
    try {
      const response = await api.getRemarks(lead.id);
      setRemarks(response.data);
    } catch (error) {
      console.error('Error fetching remarks:', error);
    }
  };

  const fetchLeadHistory = async () => {
    if (leadHistory.length > 0) {
      setShowHistory(!showHistory);
      return;
    }
    
    setLoadingHistory(true);
    try {
      const response = await api.getLeadHistory(lead.phone);
      // Filter out current lead from history
      const history = response.data.history.filter(h => h.id !== lead.id);
      setLeadHistory(history);
      setShowHistory(true);
    } catch (error) {
      toast.error('Failed to fetch lead history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    // Check if status is customer - requires product selection
    if (newStatus === 'customer') {
      setPendingStatus(newStatus);
      fetchProducts();
      return;
    }
    
    // For all other statuses (including follow_up), just update directly
    await updateStatus(newStatus);
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await api.getProducts();
      setProducts(response.data);
      setShowProductModal(true);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductSelect = async () => {
    if (!selectedProductId) {
      toast.error('Please select a product');
      return;
    }
    
    if (!conversionScreenshot) {
      toast.error('Please upload a conversion screenshot');
      return;
    }
    
    setLoading(true);
    try {
      // First upload the screenshot
      setUploadingScreenshot(true);
      const uploadResponse = await api.uploadConversionScreenshot(conversionScreenshot);
      const screenshotUrl = uploadResponse.data.url;
      setUploadingScreenshot(false);
      
      // Then update the lead with product and screenshot
      const response = await api.updateLead(lead.id, { 
        status: 'customer', 
        product_id: selectedProductId,
        conversion_screenshot: screenshotUrl
      });
      setLeadData(response.data);
      toast.success('Lead marked as Customer!');
      setShowProductModal(false);
      setSelectedProductId('');
      setPendingStatus(null);
      setConversionScreenshot(null);
      setScreenshotPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setLoading(false);
      setUploadingScreenshot(false);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setConversionScreenshot(file);
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  const removeScreenshot = () => {
    setConversionScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateStatus = async (newStatus, productId = null) => {
    try {
      const updateData = { status: newStatus };
      if (productId) updateData.product_id = productId;
      
      const response = await api.updateLead(lead.id, updateData);
      setLeadData(response.data);
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddRemark = async (e) => {
    e.preventDefault();
    if (!newRemark.trim()) return;

    try {
      await api.createRemark(lead.id, newRemark);
      setNewRemark('');
      fetchRemarks();
      toast.success('Remark added successfully');
    } catch (error) {
      toast.error('Failed to add remark');
    }
  };

  const handleSetReminder = async (e) => {
    e.preventDefault();
    if (!reminderDate) {
      toast.error('Please select a date and time');
      return;
    }

    setLoading(true);
    try {
      await api.createReminder({
        lead_id: lead.id,
        reminder_time: new Date(reminderDate).toISOString(),
        note: reminderNote
      });
      toast.success('Reminder set successfully');
      setReminderDate('');
      setReminderNote('');
    } catch (error) {
      toast.error('Failed to set reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!window.confirm(`Are you sure you want to delete lead "${leadData.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      await api.deleteLead(lead.id);
      toast.success('Lead deleted successfully');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const canEdit = user?.role !== 'caller' || leadData.assigned_to === user?.id;
  const canDelete = ['admin', 'manager'].includes(user?.role);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold font-headings text-slate-900">{leadData.name}</h2>
            <p className="text-slate-600 font-body text-sm mt-1">
              Lead ID: <span className="font-mono">{leadData.id.substring(0, 8)}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canDelete && (
              <button
                data-testid="delete-lead-button"
                onClick={handleDeleteLead}
                disabled={deleting}
                className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                title="Delete Lead"
              >
                <Trash2 size={20} />
              </button>
            )}
            <button
              data-testid="close-lead-details-modal"
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-md transition-colors"
            >
              <X size={24} className="text-slate-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Lead History Button */}
          <button
            onClick={fetchLeadHistory}
            data-testid="view-lead-history-btn"
            className="w-full flex items-center justify-between p-3 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors"
          >
            <div className="flex items-center gap-2">
              <History size={18} className="text-indigo-600" />
              <span className="font-medium text-indigo-700 font-body">
                Student History (Same Phone)
              </span>
            </div>
            {loadingHistory ? (
              <Loader2 size={16} className="animate-spin text-indigo-600" />
            ) : (
              showHistory ? <ChevronUp size={18} className="text-indigo-600" /> : <ChevronDown size={18} className="text-indigo-600" />
            )}
          </button>

          {/* Lead History Panel */}
          {showHistory && (
            <div className="bg-indigo-50/50 rounded-lg border border-indigo-200 p-4">
              {leadHistory.length === 0 ? (
                <p className="text-center text-slate-500 font-body py-4">
                  No previous entries found for this phone number
                </p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  <p className="text-xs font-bold uppercase text-indigo-700 mb-2">
                    {leadHistory.length} Previous {leadHistory.length === 1 ? 'Entry' : 'Entries'}
                  </p>
                  {leadHistory.map((entry, index) => (
                    <div 
                      key={entry.id || index} 
                      className="bg-white rounded-lg p-3 border border-indigo-100 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 truncate">{entry.name}</span>
                            <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-full border ${getStatusColor(entry.status)}`}>
                              {getStatusLabel(entry.status)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            <span className="font-medium">Course:</span> {entry.course || 'N/A'}
                          </p>
                          <p className="text-sm text-slate-600">
                            <span className="font-medium">Assigned:</span> {entry.assigned_to_name}
                          </p>
                          {entry.status === 'customer' && entry.product_name && (
                            <p className="text-sm text-emerald-600 font-medium">
                              Sold: {entry.product_name} (₹{entry.product_price?.toLocaleString()})
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-slate-500">{formatDate(entry.created_at)}</p>
                          {entry.remarks_count > 0 && (
                            <p className="text-xs text-slate-400">{entry.remarks_count} remarks</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Lead Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 font-body">Phone</label>
              <p className="text-slate-900 font-mono mt-1">{leadData.phone}</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 font-body">Email</label>
              <p className="text-slate-900 font-body mt-1">{leadData.email || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 font-body">Course</label>
              <p className="text-slate-900 font-body mt-1">{leadData.course || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 font-body">Assigned To</label>
              <p className="text-slate-900 font-body mt-1">{leadData.assigned_to_name || 'Unassigned'}</p>
            </div>
            {leadData.status === 'customer' && leadData.product_name && (
              <>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-emerald-600 font-body">Product Sold</label>
                  <p className="text-emerald-700 font-semibold mt-1 flex items-center gap-1">
                    <Package size={16} />
                    {leadData.product_name}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-emerald-600 font-body">Sale Amount</label>
                  <p className="text-emerald-700 font-semibold mt-1 flex items-center gap-1">
                    <IndianRupee size={16} />
                    {leadData.product_price?.toLocaleString() || '-'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-emerald-600 font-body">Conversion Date</label>
                  <p className="text-emerald-700 font-semibold mt-1 flex items-center gap-1">
                    <Clock size={16} />
                    {leadData.conversion_date ? new Date(leadData.conversion_date).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : (leadData.sold_at ? new Date(leadData.sold_at).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    }) : '-')}
                  </p>
                </div>
              </>
            )}
            {leadData.status === 'customer' && leadData.conversion_screenshot && (
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-emerald-600 font-body">Conversion Proof</label>
                <div className="mt-2">
                  <a 
                    href={leadData.conversion_screenshot.startsWith('http') ? leadData.conversion_screenshot : `${process.env.REACT_APP_BACKEND_URL}${leadData.conversion_screenshot}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
                  >
                    <Image size={16} />
                    View Screenshot
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 font-body mb-2 block">
              Status
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { value: 'new', label: 'New' },
                { value: 'hot', label: 'Hot' },
                { value: 'warm', label: 'Warm' },
                { value: 'not_connected', label: 'Not Connected' },
                { value: 'not_answered', label: 'Not Answered' },
                { value: 'not_interested', label: 'Not Interested' },
                { value: 'already_purchased', label: 'Already Purchased' },
                { value: 'customer', label: 'Customer' },
                { value: 'follow_up', label: 'Follow Up' },
                { value: 'follow_up_later', label: 'Follow Up Later' },
              ].map((status) => (
                <button
                  key={status.value}
                  data-testid={`status-${status.value}-button`}
                  onClick={() => canEdit && handleStatusChange(status.value)}
                  disabled={!canEdit}
                  className={`px-3 py-1.5 text-xs font-bold uppercase rounded-full border transition-all ${
                    leadData.status === status.value
                      ? getStatusColor(status.value)
                      : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                  } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Product Selection Modal for Customer Status */}
          {showProductModal && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-emerald-800 font-body mb-3 flex items-center gap-2">
                <Package size={16} />
                Mark as Customer (Required Fields)
              </h3>
              {loadingProducts ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin text-emerald-600" size={24} />
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-600 mb-2">No products available</p>
                  <p className="text-sm text-slate-500">Please add products from the Products page first.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Product Selection */}
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-2 block">1. Select Product Sold *</label>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {products.map((product) => (
                        <label
                          key={product.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedProductId === product.id
                              ? 'bg-emerald-100 border-emerald-400'
                              : 'bg-white border-slate-200 hover:border-emerald-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="product"
                              value={product.id}
                              checked={selectedProductId === product.id}
                              onChange={(e) => setSelectedProductId(e.target.value)}
                              className="w-4 h-4 text-emerald-600"
                              data-testid={`product-radio-${product.id}`}
                            />
                            <span className="font-medium text-slate-900">{product.name}</span>
                          </div>
                          <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                            <IndianRupee size={14} />
                            {product.price.toLocaleString()}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Screenshot Upload */}
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-2 block">
                      2. Upload Conversion Screenshot * 
                      <span className="font-normal text-slate-500">(Proof of sale)</span>
                    </label>
                    <div className="border-2 border-dashed border-emerald-300 rounded-lg p-4 bg-white">
                      {screenshotPreview ? (
                        <div className="relative">
                          <img 
                            src={screenshotPreview} 
                            alt="Conversion proof" 
                            className="max-h-40 mx-auto rounded-lg shadow-sm"
                          />
                          <button
                            type="button"
                            onClick={removeScreenshot}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X size={14} />
                          </button>
                          <p className="text-xs text-emerald-600 text-center mt-2">
                            {conversionScreenshot?.name}
                          </p>
                        </div>
                      ) : (
                        <div 
                          className="text-center cursor-pointer"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <div className="w-12 h-12 mx-auto mb-2 bg-emerald-100 rounded-full flex items-center justify-center">
                            <Camera size={24} className="text-emerald-600" />
                          </div>
                          <p className="text-sm text-slate-700 font-medium">Click to upload screenshot</p>
                          <p className="text-xs text-slate-500 mt-1">PNG, JPG, WEBP up to 5MB</p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleScreenshotChange}
                        className="hidden"
                        data-testid="screenshot-input"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={handleProductSelect}
                      disabled={loading || !selectedProductId || !conversionScreenshot}
                      data-testid="confirm-product-btn"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-all font-body disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>{uploadingScreenshot ? 'Uploading...' : 'Saving...'}</span>
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          <span>Mark as Customer</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProductModal(false);
                        setPendingStatus(null);
                        setSelectedProductId('');
                        removeScreenshot();
                      }}
                      className="px-4 py-2 bg-white border border-emerald-300 text-emerald-700 rounded-md hover:bg-emerald-50 transition-all font-body"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Set Reminder */}
          {user?.role === 'caller' && leadData.assigned_to === user?.id && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={18} className="text-blue-600" />
                <h3 className="font-semibold font-body text-slate-900">Set Reminder</h3>
              </div>
              <form onSubmit={handleSetReminder} className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-700 font-body mb-1 block">Date & Time</label>
                  <input
                    type="datetime-local"
                    data-testid="reminder-datetime-input"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 font-body text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 font-body mb-1 block">Note (Optional)</label>
                  <input
                    type="text"
                    data-testid="reminder-note-input"
                    value={reminderNote}
                    onChange={(e) => setReminderNote(e.target.value)}
                    placeholder="Add a note for this reminder..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 font-body text-sm"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="set-reminder-button"
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all shadow-sm font-body text-sm disabled:opacity-50"
                >
                  <Save size={16} />
                  <span>{loading ? 'Setting...' : 'Set Reminder'}</span>
                </button>
              </form>
            </div>
          )}

          {/* Remarks */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare size={18} className="text-slate-600" />
              <h3 className="font-semibold font-body text-slate-900">Remarks</h3>
            </div>

            {/* Add Remark Form */}
            <form onSubmit={handleAddRemark} className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  data-testid="new-remark-input"
                  value={newRemark}
                  onChange={(e) => setNewRemark(e.target.value)}
                  placeholder="Add a remark..."
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
                <button
                  type="submit"
                  data-testid="add-remark-button"
                  className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body"
                >
                  Add
                </button>
              </div>
            </form>

            {/* Remarks List */}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {remarks.length === 0 ? (
                <p className="text-slate-500 font-body text-sm text-center py-4">No remarks yet</p>
              ) : (
                remarks.map((remark) => (
                  <div
                    key={remark.id}
                    data-testid={`remark-${remark.id}`}
                    className="bg-slate-50 border border-slate-200 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-medium text-slate-900 font-body text-sm">
                        {remark.user_name}
                        <span className="ml-2 text-xs text-slate-500 uppercase font-body">({remark.user_role})</span>
                      </p>
                      <p className="text-xs text-slate-500 font-mono">{formatDate(remark.created_at)}</p>
                    </div>
                    <p className="text-slate-700 font-body text-sm">{remark.text}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsModal;